import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  Inject,
  inject,
  input,
  Output,
  PLATFORM_ID,
  Renderer2,
  signal,
  WritableSignal,
} from '@angular/core';
import { Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { MAT_MENU_SCROLL_STRATEGY } from '@angular/material/menu';
import { DOCUMENT, NgForOf, NgIf } from '@angular/common';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { NewArrayPipe } from '@common/pipes';
import { ImageLoadingComponent } from '@common/image-loading/image-loading.component';
import { Toast } from '@helper/toast';
import { Constants } from '@helper/constants';
import { FocusRemoverDirective } from '@helper/focus-remover.directive';
import { MenuModule } from '@module/menu-module';
import { InventoryCheck } from '@helper/inventory-check';
import { WindowDetectService } from '@service/window-detect.service';
import { NumberPickerDialogComponent } from '@common/number-picker-dialog/number-picker-dialog.component';
import { CdkPageLockComponent } from '@common/cdk-page-lock-component/cdk-page-lock.component';

import { filter } from 'rxjs';

@Component({
  selector: 'app-stepper',
  templateUrl: './stepper.component.html',
  styleUrls: ['./stepper.component.scss'],
  imports: [
    NgIf,
    NgForOf,
    NewArrayPipe,
    MenuModule,
    ImageLoadingComponent,
    FocusRemoverDirective,
    MatSnackBarModule,
    MatDialogModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      // 因全站有設定 mat-menu ScrollStrategy 捲動就會關閉已經打開的 mat-menu
      // 但 stepper 的 mat-menu 不使用該機制.
      provide: MAT_MENU_SCROLL_STRATEGY,
      deps: [Overlay],
      useFactory: (overlay: Overlay) => (): ScrollStrategy => overlay.scrollStrategies.noop(),
    },
  ],
})
export class StepperComponent extends CdkPageLockComponent {
  readonly defaultMax = Constants.MAX_CART_ITEM_COUNT;
  readonly value: WritableSignal<number>;
  readonly size = computed(() => this.actionBtnSize() || this.btnSize() - 2);
  readonly dialog = inject(MatDialog);
  readonly windowService = inject(WindowDetectService);
  readonly mobile = this.windowService.isMobileDevice();
  readonly min = input(1);
  readonly max = input(this.defaultMax);
  readonly current = input(1);
  readonly disabled = input(false);
  readonly allowSelect = input(true);
  readonly displayOnly = input(false);
  readonly stepperWidth = input(91);
  readonly btnSize = input(28);
  readonly actionBtnSize = input(0);
  readonly actionImageSize = input(9);
  readonly darkMode = input(false);
  readonly remindExceededMax = input(false);

  get maxValue(): number {
    return Math.min(this.max(), this.defaultMax);
  }

  @Output() readonly count = new EventEmitter<number>();

  constructor(
    @Inject(PLATFORM_ID) platformId: object,
    @Inject(DOCUMENT) document: Document,
    renderer: Renderer2,
    private hostElement: ElementRef,
    private toast: MatSnackBar
  ) {
    super(platformId, document, renderer);
    this.value = signal(this.current());
    afterRenderEffect(() => {
      this.hostElement.nativeElement.style.width = `${this.stepperWidth()}px`;
    });
    effect(() => {
      if (this.current()) {
        this.value.set(Math.max(this.min(), this.current()));
      } else if (this.max() || this.min()) {
        this.value.update(value => Math.max(this.min(), value));
      }
    });
  }

  setCount(opt: number): void {
    this.value.set(opt);
    this.count.next(this.value());
  }

  increase(): void {
    if (this.disabled()) {
      return;
    }
    if (this.remindExceededMax()) {
      InventoryCheck.showExceededToast(this.toast, this.value() + 1, this.maxValue);
    }
    if (!this.maxValue) {
      return;
    }
    if (this.value() > this.maxValue) {
      return;
    }
    this.value.update(value => Math.min(this.maxValue, Math.max(this.min(), value + 1)));
    this.count.next(this.value());
  }

  decrease(): void {
    if (this.disabled() || this.value() <= this.min()) {
      return;
    }
    if (!this.maxValue) {
      Toast.open(this.toast, $localize`商品庫存不足`);
      return;
    }
    this.value.update(value => Math.min(Math.min(this.max(), this.defaultMax), Math.max(this.min(), value - 1)));
    this.count.next(this.value());
  }

  numberPicker(): void {
    if (this.disabled() || this.value() < this.min() || this.value() > this.maxValue || !this.maxValue) {
      return;
    }
    NumberPickerDialogComponent.open(this.dialog, this.value(), this.maxValue, this.min(), this.remindExceededMax())
      .afterClosed()
      .pipe(filter(res => !!res))
      .subscribe(res => {
        if (res) {
          this.value.set(Math.min(Math.min(this.maxValue, res), Math.max(this.min(), res)));
          this.count.next(this.value());
        }
      });
  }
}
